// Küçük bir PNG okuyucu ve yazıcı (9.32 · D-653).
//
// NEDEN KENDİMİZ: denenen hazır kütüphane (fast-png) yüklenir yüklenmez
// `new TextDecoder('latin1')` çağırıyor. Expo'nun telefondaki TextDecoder'ı
// yalnız UTF-8 tanıyor; uygulama bu dosyayı içe aktardığı anda — yani sohbet
// ekranı açılırken — düşerdi. Testler bunu yakaladı. Sıkıştırma için fflate
// kullanılıyor (saf JavaScript, yalnız UTF-8 çözücü kuruyor).
//
// Kapsam bilerek dar: logolar ve paylaşım görselleri için yeterli olan
// taramasız (non-interlaced) PNG'ler; 1–16 bit, gri, renkli, paletli ve
// saydam. Taramalı PNG null döner; o zaman logo olmadan devam edilir.
import { unzlibSync, zlibSync } from 'fflate';

export type RgbaImage = { width: number; height: number; data: Uint8Array };

const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const CHANNELS: Record<number, number> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };

function readUint32(bytes: Uint8Array, at: number): number {
  return ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;
}

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  return pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
}

/** Taramasız PNG'yi RGBA'ya çevir; desteklenmeyen ya da bozuk dosyada null. */
export function decodePng(bytes: Uint8Array): RgbaImage | null {
  if (bytes.length < 33 || SIGNATURE.some((b, i) => bytes[i] !== b)) return null;
  let width = 0; let height = 0; let depth = 0; let colorType = -1; let interlace = 0;
  let palette: Uint8Array | null = null;
  let transparency: Uint8Array | null = null;
  const idat: Uint8Array[] = [];
  for (let at = 8; at + 12 <= bytes.length;) {
    const length = readUint32(bytes, at);
    const type = String.fromCharCode(bytes[at + 4], bytes[at + 5], bytes[at + 6], bytes[at + 7]);
    const body = bytes.subarray(at + 8, at + 8 + length);
    if (body.length !== length) return null;
    if (type === 'IHDR') {
      width = readUint32(body, 0); height = readUint32(body, 4);
      depth = body[8]; colorType = body[9]; interlace = body[12];
    } else if (type === 'PLTE') palette = body;
    else if (type === 'tRNS') transparency = body;
    else if (type === 'IDAT') idat.push(body);
    else if (type === 'IEND') break;
    at += 12 + length;
  }
  const channels = CHANNELS[colorType];
  if (!channels || !width || !height || interlace !== 0 || ![1, 2, 4, 8, 16].includes(depth)) return null;
  if (width * height > 8_000_000) return null;
  if (colorType === 3 && !palette) return null;

  let joined = 0;
  for (const part of idat) joined += part.length;
  const compressed = new Uint8Array(joined);
  for (let o = 0, i = 0; i < idat.length; o += idat[i].length, i++) compressed.set(idat[i], o);
  let raw: Uint8Array;
  try {
    raw = unzlibSync(compressed);
  } catch {
    return null;
  }

  const bitsPerPixel = depth * channels;
  const stride = Math.ceil((width * bitsPerPixel) / 8);
  const bpp = Math.max(1, bitsPerPixel >> 3);
  if (raw.length < height * (stride + 1)) return null;
  const lines = new Uint8Array(height * stride);
  for (let y = 0; y < height; y++) {
    const filter = raw[y * (stride + 1)];
    const src = y * (stride + 1) + 1;
    const out = y * stride;
    const prev = out - stride;
    for (let x = 0; x < stride; x++) {
      const left = x >= bpp ? lines[out + x - bpp] : 0;
      const up = y > 0 ? lines[prev + x] : 0;
      const upLeft = y > 0 && x >= bpp ? lines[prev + x - bpp] : 0;
      const value = raw[src + x];
      let decoded: number;
      if (filter === 0) decoded = value;
      else if (filter === 1) decoded = value + left;
      else if (filter === 2) decoded = value + up;
      else if (filter === 3) decoded = value + ((left + up) >> 1);
      else if (filter === 4) decoded = value + paeth(left, up, upLeft);
      else return null;
      lines[out + x] = decoded & 0xff;
    }
  }

  // Örnekleri 8 bite indir: 16 bitte üst bayt, 1/2/4 bitte ölçekle.
  const maxSample = (1 << depth) - 1;
  const sample = (y: number, index: number): number => {
    const row = y * stride;
    if (depth === 8) return lines[row + index];
    if (depth === 16) return lines[row + index * 2];
    const bit = index * depth;
    const byte = lines[row + (bit >> 3)];
    const value = (byte >> (8 - depth - (bit & 7))) & maxSample;
    return colorType === 3 ? value : Math.round((value * 255) / maxSample);
  };

  const data = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      const s = x * channels;
      if (colorType === 3) {
        const index = sample(y, s);
        data[o] = palette![index * 3] ?? 0;
        data[o + 1] = palette![index * 3 + 1] ?? 0;
        data[o + 2] = palette![index * 3 + 2] ?? 0;
        data[o + 3] = transparency && index < transparency.length ? transparency[index] : 255;
      } else if (colorType === 0 || colorType === 4) {
        const gray = sample(y, s);
        data[o] = gray; data[o + 1] = gray; data[o + 2] = gray;
        data[o + 3] = colorType === 4 ? sample(y, s + 1) : 255;
      } else {
        data[o] = sample(y, s); data[o + 1] = sample(y, s + 1); data[o + 2] = sample(y, s + 2);
        data[o + 3] = colorType === 6 ? sample(y, s + 3) : 255;
      }
    }
  }
  return { width, height, data };
}

let crcTable: Uint32Array | null = null;

function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function chunk(type: string, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + body.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, body.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(body, 8);
  view.setUint32(8 + body.length, crc32(out.subarray(4, 8 + body.length)));
  return out;
}

/** RGBA'yı 8 bitlik PNG'ye yaz (satır başına "Sub" süzgeci). */
export function encodePng(image: RgbaImage): Uint8Array {
  const { width, height, data } = image;
  const stride = width * 4;
  const raw = new Uint8Array(height * (stride + 1));
  for (let y = 0; y < height; y++) {
    const at = y * (stride + 1);
    raw[at] = 1;
    for (let x = 0; x < stride; x++) {
      const value = data[y * stride + x];
      const left = x >= 4 ? data[y * stride + x - 4] : 0;
      raw[at + 1 + x] = (value - left) & 0xff;
    }
  }
  const header = new Uint8Array(13);
  const hv = new DataView(header.buffer);
  hv.setUint32(0, width); hv.setUint32(4, height);
  header[8] = 8; header[9] = 6; // 8 bit, RGBA
  const parts = [
    Uint8Array.from(SIGNATURE),
    chunk('IHDR', header),
    chunk('IDAT', zlibSync(raw, { level: 9 })),
    chunk('IEND', new Uint8Array(0)),
  ];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  for (let o = 0, i = 0; i < parts.length; o += parts[i].length, i++) out.set(parts[i], o);
  return out;
}
